// ============================================================================
// T-CAL-4 · R-TEM-01 · LA TEMPERATURA SE VE — el chip y su porqué.
// UNA sola pieza para la tarjeta del embudo y la ficha lateral: si cada pantalla
// armara su propio texto, la misma temperatura se diría distinto en dos sitios.
//
// ⛔ SOLO PINTA lo que trajo la carga (`v_embudo`, migr `0070`): aquí no se calcula
//    ni se enfría nada — eso lo hace la base con UNA regla (I-108). Y no se
//    suscribe a nada en vivo: el chip no se mueve bajo la mano (I-12).
// ⛔ «frío» es DISCRETO (tono neutro): un cliente frío no es una alarma. El único
//    chip sólido de la tarjeta sigue siendo «Esperando asesor».
// ============================================================================
import type { Temperatura } from "./tipos.ts";

/** Lo que la pantalla necesita saber; sale tal cual de la fila de `v_embudo`. */
export interface DatoTemperatura {
  temperatura?: Temperatura | null;
  temperatura_motivo?: string | null;
  temperatura_enfriada?: boolean | null;
}

const NOMBRE: Record<Temperatura, string> = { caliente: "caliente", tibio: "tibio", frio: "frío" };
// ⚠️ CONTRASTE MEDIDO (26-sep, AA pide 4,5:1 para 11 px): los tonos `alerta`
//    (3,2:1) y `neutro` (4,4:1) del `Chip` común NO llegan en CLARO, así que aquí
//    la letra de tibio y frío es `tinta` (la del texto normal) sobre un fondo
//    tenue: tibio ámbar al 15 %, frío gris. Caliente sí usa el par de `marca`
//    (`acento` sobre `marca-suave`: 5,5:1 claro · 5,6:1 oscuro). Mismos tokens de
//    siempre, ningún color nuevo; mismas medidas de chip que `piezas.tsx:Chip`.
const TONO: Record<Temperatura, string> = {
  caliente: "bg-marca-suave text-acento",
  tibio: "bg-alerta/15 text-tinta",
  frio: "bg-tenue text-tinta",
};

/** La vista no trae cuántos días lleva sin escribir (solo si se enfrió), así que
 *  se dice sin cifra: inventar «4 días» sería afirmar un dato que no tenemos. */
export const SE_ENFRIO = "se enfrió: el cliente dejó de escribir";

/** ¿Hay algo que pintar? Solo uno de los tres valores conocidos. */
export function hayTemperatura(d: DatoTemperatura | null | undefined): d is DatoTemperatura & { temperatura: Temperatura } {
  return !!d && (d.temperatura === "caliente" || d.temperatura === "tibio" || d.temperatura === "frio");
}

/** El porqué en palabras: el motivo y, si aplica, que se enfrió. `null` sin temperatura. */
export function porqueTemperatura(d: DatoTemperatura | null | undefined): string | null {
  if (!hayTemperatura(d)) return null;
  const motivo = d.temperatura_motivo?.trim();
  const partes = [motivo, d.temperatura_enfriada ? SE_ENFRIO : null].filter(Boolean);
  return partes.length ? partes.join(" · ") : null;
}

/** Lo que se añade AL FINAL del `aria-label` de la tarjeta (I-111). Vacío sin temperatura. */
export function textoTemperatura(d: DatoTemperatura | null | undefined): string {
  if (!hayTemperatura(d)) return "";
  const porque = porqueTemperatura(d);
  return ` · Temperatura ${NOMBRE[d.temperatura]}${porque ? `: ${porque}` : ""}`;
}

/** El chip: 🔥 / tibio / frío, con el porqué en el `title`. Sin temperatura, nada. */
export function ChipTemperatura({ d }: { d: DatoTemperatura | null | undefined }) {
  if (!hayTemperatura(d)) return null;
  const t = d.temperatura;
  const porque = porqueTemperatura(d);
  return (
    <span data-temperatura={t} className="shrink-0 inline-flex">
      <span title={porque ? `${NOMBRE[t]} · ${porque}` : NOMBRE[t]}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${TONO[t]}`}>
        {t === "caliente"
          ? <><span aria-hidden="true">🔥</span><span className="sr-only">caliente</span></>
          : NOMBRE[t]}
      </span>
    </span>
  );
}
